import { z } from 'zod';
import { personalDetailsPatchSchema } from './users.js';
/**
 * Profile change requests (REQUIREMENTS §2.6 "soldier requests changes";
 * owner, 2026-09-13: hr or admin approves, both are told about the request).
 *
 * A soldier never edits their own file directly. They ask for a change; the
 * request carries exactly the patch an approver would otherwise type, and
 * approving applies it through the same code path as a direct edit, so the
 * two can never drift apart.
 */
export const profileRequestStatusSchema = z.enum(['pending', 'approved', 'rejected']);
/** What a soldier may ask to change: contact details and personal details. Nothing about identity, rank, team or roles. */
export const profileRequestPatchSchema = z
    .object({
    phone: z.string().max(40).nullable(),
    personalDetails: personalDetailsPatchSchema,
})
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'At least one field must be supplied' });
export const profileRequestSchema = z.object({
    id: z.string().min(1),
    uid: z.string().min(1),
    soldierName: z.string(),
    personalNumber: z.string(),
    /** Where the soldier sat when they asked; for the approver's link to the file. */
    teamId: z.string().nullable(),
    platoonId: z.string().nullable().default(null),
    patch: profileRequestPatchSchema,
    /** The soldier's own words about why, shown to the approver. */
    note: z.string().max(500).nullable(),
    status: profileRequestStatusSchema,
    requestedAt: z.string().nullable(),
    decidedBy: z.string().nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.string().nullable(),
    /** The approver's reason, mandatory on rejection. */
    reason: z.string().max(500).nullable(),
});
/** POST /api/me/profile-requests — the caller asks for a change to their own file. */
export const createProfileRequestSchema = z.object({
    patch: profileRequestPatchSchema,
    note: z.string().trim().max(500).nullable().default(null),
});
/** GET /api/profile-requests?status= — the approvers' queue. */
export const profileRequestsQuerySchema = z.object({
    status: profileRequestStatusSchema.default('pending'),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
export const profileRequestsResponseSchema = z.object({
    requests: z.array(profileRequestSchema),
});
export const profileRequestResponseSchema = z.object({ request: profileRequestSchema });
/** POST /api/profile-requests/:id/reject — a rejection says why. */
export const rejectProfileRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** One open request at a time: the second would only confuse the approver about which one stands. */
export const MAX_OPEN_PROFILE_REQUESTS = 1;
//# sourceMappingURL=profileRequests.js.map